import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EnergiesService, Range, Inverter } from 'ngx-core-services';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { of, from } from 'rxjs';

import {
  AppState,
  SystemsState,
  getSystemDefinitionDeviceIdsForTelemetries,
  getSystemDefinitionConfiguration,
  requestSystemMonthlyYearlyEnergy,
  selectedSystemMetadata,
  setSystemEnergyStateError,
  updateSystemMonthlyYearlyEnergy,
} from '..';

@Injectable()
export class MonthlyYearlyEnergyEffects {

  requestMonthlyYearlyEnergyState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemMonthlyYearlyEnergy),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) =>
        from(this.getMonthlyYearlyEnergyState(
            action.systemId,
            action.range,
            action.mode,
            action.forceRefresh,
            systemState,
            action.deviceId
          )
        ).pipe(
          map((systemEnergyState: {
            energies: Partial<Inverter>[],
            mode: 'month' | 'year'
            deviceId?: string,
          }) => updateSystemMonthlyYearlyEnergy({ systemId: action.systemId, instanceId: action.instanceId, range: action.range, systemEnergyState: systemEnergyState.energies, mode: systemEnergyState.mode, deviceId: systemEnergyState.deviceId })),
          catchError(_ => of(setSystemEnergyStateError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system energy state' })))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly energiesService: EnergiesService,
  ) {}

  async getMonthlyYearlyEnergyState(systemId: string, range: Range, mode: 'year' | 'month',  forceRefresh: boolean, state: SystemsState, deviceId?: string): Promise<{
    energies: Partial<Inverter>[],
    mode: 'month' | 'year'
    deviceId?: string,
  }> {
    const system = state.systemList[systemId];

    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);

    const serials = deviceId ? [deviceId] : getSystemDefinitionDeviceIdsForTelemetries(system);
    const energies = await this.energiesService.fetchEnergiesByRange(serials, systemConfiguration.monitoringType, range, forceRefresh);
    let aggregateEnergies = this.energiesService.getEnergyAggregate(energies, !!systemConfiguration.inverterMeterSensor);
    return {
      energies: aggregateEnergies,
      mode,
      deviceId,
    };
  }
}
